import React, { useState, useMemo, useCallback, useRef, useEffect, isValidElement } from 'react'
import ReactDOM from 'react-dom'
import { Table, Input, Checkbox, Button, Segment, Header } from 'semantic-ui-react'
import { TableToolbar } from './index'
import { useZoom } from './zoomStore'

const DEFAULT_PAGE_SIZE = 25

const WEEK_DAYS = [
	{ key: 1, label: 'L' },
	{ key: 2, label: 'M' },
	{ key: 3, label: 'X' },
	{ key: 4, label: 'J' },
	{ key: 5, label: 'V' },
	{ key: 6, label: 'S' },
	{ key: 0, label: 'D' },
]

const normalizeDaysArray = (value) =>
	Array.isArray(value) ? value.map((d) => Number(d)) : []

const getNestedValue = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj) ?? ''

const isDarkColor = (hex) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return false
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16) / 255
  const g = parseInt(h.slice(2, 4), 16) / 255
  const b = parseInt(h.slice(4, 6), 16) / 255
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 0.5
}

const SearchableSelect = ({ value, options, onChange, onBlur, autoFocus, displayValue = null }) => {
  const inputRef = useRef(null)
  const [open, setOpen] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [position, setPosition] = useState(null)

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
    if (open) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect()
          setPosition({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 140) })
        }
      })
    }
  }, [open, autoFocus])

  const filteredOptions = useMemo(() => {
    if (!searchText) return options
    const q = searchText.toLowerCase()
    return options.filter(o => {
      const label = String(o.text || o.label || o.value || '').toLowerCase()
      return label.includes(q)
    })
  }, [options, searchText])

  const handleSelect = (o) => {
    onChange(o.value)
    onBlur?.()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0
      if (filteredOptions[idx]) handleSelect(filteredOptions[idx])
    } else if (e.key === 'Escape') {
      onBlur?.()
    }
  }

  return (
    <div style={{ position: 'relative', minWidth: 140 }}>
      <input
        ref={inputRef}
        value={searchText}
        onChange={e => { setSearchText(e.target.value); setHighlightedIndex(-1) }}
        onKeyDown={handleKeyDown}
        onBlur={() => setOpen(false)}
        onClick={e => {
          e.stopPropagation(); setOpen(true)
          requestAnimationFrame(() => {
            if (inputRef.current) {
              const rect = inputRef.current.getBoundingClientRect()
              setPosition({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 140) })
            }
          })
        }}
        placeholder={displayValue ? String(displayValue) : value ? String(value) : 'Buscar...'}
        style={{ width: '100%', padding: '2px 4px', border: '1px solid #6366f1', borderRadius: 3, fontSize: 9, outline: 'none' }}
      />
      {open && position && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: 220,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #d0d0d0',
            borderRadius: 3,
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          {filteredOptions.length === 0 && (
            <div style={{ padding: '3px 6px', fontSize: 9, color: '#999' }}>Sin resultados</div>
          )}
          {filteredOptions.map((o, i) => {
            const selected = String(o.value) === String(value)
            const highlighted = i === highlightedIndex
            return (
              <div
                key={o.value}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(o) }}
                onMouseEnter={() => setHighlightedIndex(i)}
                style={{
                  padding: '3px 6px', fontSize: 9, cursor: 'pointer',
                  background: highlighted ? '#e8f0fe' : selected ? '#f0f7ff' : '#fff',
                  fontWeight: selected ? 700 : 400,
                  color: highlighted ? '#1a73e8' : '#000',
                }}
              >
                {o.text || o.label || o.value}
              </div>
            )
          })}
        </div>,
        document.body
      )}
    </div>
  )
}

const WeekDaysToggle = ({ value = [], onChange, editable = true }) => {
  const days = normalizeDaysArray(value)

  const toggle = (key) => {
    if (!editable || !onChange) return
    const next = days.includes(key)
      ? days.filter((d) => d !== key)
      : [...days, key]
    onChange(next)
  }

  return (
    <div style={{ display: 'inline-flex', gap: 2 }}>
      {WEEK_DAYS.map(({ key, label }) => {
        const active = days.includes(key)
        return (
          <button
            key={key}
            type="button"
            onClick={(e) => { e.stopPropagation(); toggle(key) }}
            disabled={!editable}
            title={label}
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              border: '1px solid #cbd5e1',
              background: active ? '#4285f4' : '#fff',
              color: active ? '#fff' : '#64748b',
              cursor: editable ? 'pointer' : 'default',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

const InlineEdit = ({ value, type, name, options, onChange, onBlur, autoFocus, displayValue = null }) => {
  const inputRef = useRef(null)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
      if (type === 'text' || type === 'number') {
        inputRef.current.select()
      }
    }
  }, [autoFocus, type])

  if (type === 'boolean') {
    return (
      <Checkbox
        checked={!!value}
        onChange={(_, { checked }) => onChange(checked)}
        onClick={(e) => e.stopPropagation()}
      />
    )
  }

  if (type === 'select' && options) {
    return (
      <SearchableSelect value={value} options={options} onChange={onChange} onBlur={onBlur} autoFocus={autoFocus} displayValue={displayValue} />
    )
  }

 if (type === 'password') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <input
          ref={inputRef}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={value ?? ''}
          autoComplete="new-password"
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            padding: '2px 4px',
            border: '1px solid #6366f1',
            borderRadius: 3,
            fontSize: 9,
            outline: 'none',
          }}
        />
        <Button
          type="button"
          icon={showPassword ? 'eye slash' : 'eye'}
          size="mini"
          basic
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowPassword((visible) => !visible)}
          aria-label={
            showPassword
              ? 'Ocultar contraseña'
              : 'Mostrar contraseña'
          }
        />
      </div>
    )
  }

  if (type === 'date') {
    return (
      <input
        ref={inputRef}
        type="date"
        value={value ? String(value).slice(0, 10) : ''}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', padding: '2px 4px', border: '1px solid #6366f1', borderRadius: 3, fontSize: 9, outline: 'none' }}
      />
    )
  }

	if (type === 'time') {
    return (
      <input
        ref={inputRef}
        type="time"
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', padding: '2px 4px', border: '1px solid #6366f1', borderRadius: 3, fontSize: 9, outline: 'none' }}
      />
    )
  }

  if (type === 'number') {
    return (
      <input
        ref={inputRef}
        type="number"
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        onBlur={onBlur}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', padding: '2px 4px', border: '1px solid #6366f1', borderRadius: 3, fontSize: 9, outline: 'none' }}
      />
    )
  }

  if (type === 'digits') {
    return (
      <input
        ref={inputRef}
        name={name}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value ?? ''}
        onChange={e => onChange(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={onBlur}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', padding: '2px 4px', border: '1px solid #6366f1', borderRadius: 3, fontSize: 9, outline: 'none' }}
      />
    )
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      onClick={e => e.stopPropagation()}
      style={{ width: '100%', padding: '2px 4px', border: '1px solid #6366f1', borderRadius: 3, fontSize: 9, outline: 'none' }}
    />
  )
}

const EditableSimpleTable = ({
  columns = [],
  rows = [],
  loading = false,
  title = '',
  onAddRow = null,
  onSave = null,
  onCancel = null,
  onDeleteRow = null,
  onCellChange = null,
  onExportAll = null,
  onRefresh = null,
  exportFilename = 'export.csv',
  searchValue = '',
  onSearchChange = null,
  searchPlaceholder = 'Buscar...',
  filterValues = {},
  onFilterChange = null,
  tableId = 'editable-table',
  serverSide = false,
  pagination = null,
  onPageChange = null,
  extraActions = [],
  isSaving = false,
  isDirty = true,
  pageSize = DEFAULT_PAGE_SIZE,
  hideTableWhenEmpty = false,
  emptyMessage = 'No hay datos',
  onRowClick = null,
  onDelete = null,
  customActions = null,
  fetchAll = null,
}) => {

  const zoom = useZoom()
  const [currentPage, setCurrentPage] = useState(1)
  const [editingCell, setEditingCell] = useState(null)
  const [activeFilter, setActiveFilter] = useState(null)
  const [filterSearchText, setFilterSearchText] = useState('')
  const [localChecked, setLocalChecked] = useState({})
  const [clickCoords, setClickCoords] = useState({ x: 0, y: 0 })
  const [globalFilterValues, setGlobalFilterValues] = useState({})
  const [fetchingFilterValues, setFetchingFilterValues] = useState(false)
  const filterSearchTimer = useRef(null)
  const cumulativeValues = useRef({})
  const localCheckedRef = useRef({})
  const filterRef = useRef(null)

  useEffect(() => { localCheckedRef.current = localChecked }, [localChecked])

  useEffect(() => {
    setLocalChecked(prev => {
      const next = {}
      Object.keys(filterValues).forEach(k => {
        if (filterValues[k] !== undefined && filterValues[k] !== null) next[k] = filterValues[k]
      })
      return next
    })
  }, [JSON.stringify(filterValues)])

  useEffect(() => { setFilterSearchText('') }, [activeFilter])

  useEffect(() => {
    if (!activeFilter) return
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setActiveFilter(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [activeFilter])

  const getSavedColumnOrder = () => {
    try {
      const saved = localStorage.getItem(`editable-columns-${tableId}`)
      if (saved) {
        const order = JSON.parse(saved)
        if (Array.isArray(order) && order.length === columns.length) {
          const hasAll = columns.every(c => order.includes(c.accessor))
          if (hasAll) return order
        }
      }
    } catch (e) {}
    return null
  }

  const [columnOrder, setColumnOrder] = useState(() => getSavedColumnOrder())

  const dragItem = useRef(null)
  const dragOverItem = useRef(null)

  const orderedColumns = useMemo(() => {
    if (columnOrder) {
      const ordered = columnOrder.map(acc => columns.find(c => c.accessor === acc)).filter(Boolean)
      return ordered.length === columns.length ? ordered : columns
    }
    return columns
  }, [JSON.stringify(columns), columnOrder])

  const hasActiveFilters = Object.keys(filterValues).length > 0

  const columnUniqueValues = useMemo(() => {
    const result = {}
    columns.forEach(col => {
      if (!col.filterable) return
      const seen = new Set()
      const vals = []
      const prev = cumulativeValues.current[col.accessor] || []
      prev.forEach(v => { const s = String(v ?? ''); if (!seen.has(s)) { seen.add(s); vals.push(v) } })
      rows.forEach(r => {
        const v = getNestedValue(r, col.accessor)
        const s = String(v ?? '')
        if (!seen.has(s)) { seen.add(s); vals.push(v) }
      })
      result[col.accessor] = vals.sort()
    })
    if (serverSide) {
      cumulativeValues.current = { ...cumulativeValues.current, ...result }
    } else {
      cumulativeValues.current = result
    }
    return result
  }, [JSON.stringify(columns), rows])

  const getFilterLabel = (col, v) => {
    if (col?.type === 'select' && Array.isArray(col?.options)) {
      const opt = col.options.find(o => String(o.value) === String(v))
      if (opt) return String(opt.text ?? opt.label ?? opt.value)
    }
    if (col?.labelAccessor) {
      const row = rows.find(r => String(getNestedValue(r, col.accessor)) === String(v))
      if (row) {
        const label = getNestedValue(row, col.labelAccessor)
        if (label) return String(label)
      }
    }
    return String(v ?? '')
  }

  const toggleFilterValue = (accessor, value) => {
    if (!onFilterChange) return
    const current = localCheckedRef.current[accessor]
    const hasActiveFilter = Array.isArray(current) && current.length > 0
    const str = String(value ?? '')
    if (!hasActiveFilter) {
      setLocalChecked(prev => ({ ...prev, [accessor]: [value] }))
      onFilterChange(accessor, [value])
      setGlobalFilterValues(prev => ({ ...prev, [accessor]: undefined }))
      setFilterSearchText('')
      return
    }
    const idx = current.findIndex(s => String(s ?? '') === str)
    let next
    if (idx >= 0) next = current.filter((_, i) => i !== idx)
    else next = [...current, value]
    setLocalChecked(prev => ({ ...prev, [accessor]: next.length ? next : [] }))
    onFilterChange(accessor, next.length ? next : null)
    setGlobalFilterValues(prev => ({ ...prev, [accessor]: undefined }))
    setFilterSearchText('')
  }

  const clearFilters = () => {
    if (onFilterChange) { onFilterChange(null, null); setLocalChecked({}) }
  }

  const handleDragStart = (index) => {
    dragItem.current = index
  }

  const handleDragEnter = (index) => {
    dragOverItem.current = index
  }

  const handleDragEnd = () => {
    if (dragItem.current == null || dragOverItem.current == null) return
    const cols = [...orderedColumns]
    const draggedCol = cols.splice(dragItem.current, 1)[0]
    cols.splice(dragOverItem.current, 0, draggedCol)
    const newOrder = cols.map(c => c.accessor)
    setColumnOrder(newOrder)
    try { localStorage.setItem(`editable-columns-${tableId}`, JSON.stringify(newOrder)) } catch (e) {}
    dragItem.current = null
    dragOverItem.current = null
  }

  const totalRows = rows.length
  const totalPages = serverSide
    ? (pagination?.total_pages || pagination?.totalPages || 1)
    : Math.max(1, Math.ceil(totalRows / pageSize))

  const visibleRows = useMemo(() => {
    if (serverSide) return rows
    const start = (currentPage - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [rows, currentPage, serverSide, pageSize])

  const handlePageChange = useCallback((newPage) => {
    if (serverSide) {
      onPageChange?.(newPage)
    } else {
      setCurrentPage(newPage)
    }
  }, [serverSide, onPageChange])

  const displayPagination = serverSide ? pagination : {
    current_page: currentPage,
    total_pages: totalPages,
    total_records: totalRows,
  }

  const handleCellClick = (rowIdx, col) => {
    if (!col.editable) return
    setEditingCell({ row: rowIdx, accessor: col.accessor })
  }

  const handleCellChange = (rowIdx, accessor, value) => {
    onCellChange?.(rowIdx, accessor, value)
  }

  const handleCellBlur = () => {
    setEditingCell(null)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setEditingCell(null)
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      setEditingCell(null)
    }
  }

  const actionButtons = [...extraActions]
  if (onSave) {
    actionButtons.push({
      label: 'Guardar',
      icon: 'save',
      action: onSave,
      disabled: isSaving || !isDirty,
    })
  }
  if (onCancel) {
    actionButtons.push({
      label: 'Cancelar',
      icon: 'undo',
      action: onCancel,
      disabled: !isDirty,
    })
  }

  const pageNumbers = useMemo(() => {
    const tp = totalPages
    const cp = displayPagination?.current_page || 1
    const pages = []
    if (tp <= 7) { for (let i = 1; i <= tp; i++) pages.push(i) }
    else {
      pages.push(1)
      let s = Math.max(2, cp - 2), e = Math.min(tp - 1, cp + 2)
      if (cp <= 3) e = 5
      if (cp >= tp - 2) s = tp - 4
      if (s > 2) pages.push('...')
      for (let i = s; i <= e; i++) pages.push(i)
      if (e < tp - 1) pages.push('...')
      pages.push(tp)
    }
    return pages
  }, [totalPages, displayPagination?.current_page])

  return (
	<div className='EditableSimpleTable'>
      <style>{`
        .editable-table-actions .ui.button {
          background: transparent !important;
          color: #4a6fa5 !important;
          box-shadow: none !important;
        }
        .editable-table-actions .ui.button:hover {
          background: rgba(74, 111, 165, 0.1) !important;
          color: #2c5282 !important;
        }
        .editable-table-actions .ui.red.button {
          color: #c53030 !important;
          background: transparent !important;
        }
        .editable-table-actions .ui.red.button:hover {
          background: rgba(197, 48, 48, 0.1) !important;
          color: #9b2c2c !important;
        }
      `}</style>
      <TableToolbar
        title={title}
        onAdd={onAddRow}
        addText="Agregar fila"
        onExportAll={onExportAll}
        exportFilename={exportFilename}
        onRefresh={onRefresh}
        extraActions={actionButtons}
      />
      {pagination?.total_records != null && (
        <div style={{ fontSize: 9, color: '#888', padding: '2px 0' }}>
          {pagination.total_records} registros
        </div>
      )}
      {onSearchChange && (
        <div style={{ display: 'flex', gap: 8, padding: '2px 0 6px 0', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
             <input
               name={`editable-table-search-${tableId}`}
               autoComplete="off"
               placeholder={searchPlaceholder}
               value={searchValue || ''}
              onChange={e => onSearchChange(e.target.value)}
              style={{
                width: '100%', padding: '5px 8px 5px 26px',
                border: '1px solid #d0d0d0', borderRadius: 4,
                fontSize: 11, outline: 'none',
              }}
            />
            <span style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#999' }}>🔍</span>
            </div>
          </div>
      )}
      {hasActiveFilters && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', marginBottom: 4 }}>
          <button onClick={clearFilters} style={{ border: '1px solid #ef4444', borderRadius: 4, padding: '2px 10px', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', fontSize: 10 }}>
            Limpiar Filtros ({Object.keys(filterValues).length})
          </button>
        </div>
      )}
      {activeFilter && (
        <div className="simple-table-filter-overlay" onClick={() => { setActiveFilter(null); if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current) }} style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="simple-table-filter-panel" ref={filterRef} onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 8, padding: '16px 20px',
            minWidth: 220, maxWidth: 360, maxHeight: '60vh', overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>
                {columns.find(c => c.accessor === activeFilter)?.Header}
              </span>
              <button onClick={() => { setActiveFilter(null); if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current) }}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#999', padding: 0, lineHeight: 1 }}>
                ✕
              </button>
            </div>
            {fetchingFilterValues && (
              <div style={{ fontSize: 11, color: '#6366f1', marginBottom: 8, textAlign: 'center' }}>
                Cargando valores...
              </div>
            )}
            <input
              className="simple-table-filter-search-input"
              placeholder="Filtrar valores..."
              value={filterSearchText}
              onChange={e => {
                const text = e.target.value
                setFilterSearchText(text)
                if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current)
                if (!activeFilter || !serverSide || !fetchAll || text.trim().length < 2) {
                  setGlobalFilterValues(prev => ({ ...prev, [activeFilter]: undefined }))
                  setFetchingFilterValues(false)
                  return
                }
                setFetchingFilterValues(true)
                filterSearchTimer.current = setTimeout(async () => {
                  try {
                    const allData = await fetchAll(text.trim(), true)
                    const unique = [...new Set((allData || []).map(r => getNestedValue(r, activeFilter)))].sort()
                    setGlobalFilterValues(prev => ({ ...prev, [activeFilter]: unique }))
                  } catch (err) {
                    setGlobalFilterValues(prev => ({ ...prev, [activeFilter]: [] }))
                  } finally {
                    setFetchingFilterValues(false)
                  }
                }, 300)
              }}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 11, outline: 'none', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button className="simple-table-filter-action" onClick={() => {
                const allVals = columnUniqueValues[activeFilter]
                setLocalChecked(prev => ({ ...prev, [activeFilter]: allVals }))
                onFilterChange?.(activeFilter, null)
              }} style={{ border: '1px solid #6366f1', borderRadius: 4, padding: '2px 8px', background: '#fff', color: '#6366f1', cursor: 'pointer', fontSize: 10 }}>
                Seleccionar todos
              </button>
              <button className="simple-table-filter-action" onClick={() => {
                setLocalChecked(prev => ({ ...prev, [activeFilter]: [] }))
              }} style={{ border: '1px solid #ef4444', borderRadius: 4, padding: '2px 8px', background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 10 }}>
                Deseleccionar todos
              </button>
            </div>
            {(Array.isArray(globalFilterValues[activeFilter])
              ? globalFilterValues[activeFilter]
              : columnUniqueValues[activeFilter] || []
            )
              .filter(v => !filterSearchText || getFilterLabel(columns.find(c => c.accessor === activeFilter), v).toLowerCase().includes(filterSearchText.toLowerCase()))
              .map((v, i) => {
              const vals = filterValues[activeFilter]
              const localVals = localChecked[activeFilter]
              const hasFilter = vals !== undefined && vals !== null
              const hasLocalFilter = Array.isArray(localVals)
              const str = String(v ?? '')
              const checked = hasLocalFilter
                  ? localVals.some(s => String(s ?? '') === str)
                  : hasFilter
                      ? vals.some(s => String(s ?? '') === str)
                      : true
              return (
                <label key={i} className="simple-table-filter-option" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
                  <Checkbox checked={checked} onChange={() => toggleFilterValue(activeFilter, v)} style={{ margin: 0 }} />
                  <span>{v == null || v === '' ? '(vacío)' : getFilterLabel(columns.find(c => c.accessor === activeFilter), v)}</span>
                </label>
              )
            })}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              {(filterValues[activeFilter] && filterValues[activeFilter].length > 0) && (
                <button className="simple-table-filter-action simple-table-filter-clear" onClick={() => { setLocalChecked(prev => ({ ...prev, [activeFilter]: [] })); onFilterChange?.(activeFilter, null); setActiveFilter(null) }} style={{ border: '1px solid #ef4444', borderRadius: 4, padding: '4px 12px', background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {hideTableWhenEmpty && !loading && rows.length === 0 ? (
        <Segment><Header as="h4" textAlign="center" style={{ color: '#888' }}>{emptyMessage}</Header></Segment>
      ) : (
        <>
      <div style={{
        overflowX: 'auto',
        maxHeight: rows.length > 15 ? '55vh' : undefined,
          overflowY: rows.length > 15 ? 'auto' : 'visible',
        }}>
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
		<Table className='EditableSimpleTable__table' compact celled size="small" style={{ fontSize: 11, margin: 0 }}>
           <thead>
             <tr>
               {(onRowClick || onDelete || onDeleteRow || customActions) && (
                <th style={{ width: 80, textAlign: 'center', padding: '2px 6px', fontSize: 11, fontWeight: 700 }}>Acciones</th>
              )}
              {orderedColumns.map((col, index) => (
                <th
                  key={col.accessor}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  style={{
                    width: col.width || 150,
                    minWidth: col.width || 100,
                    padding: '2px 6px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'grab',
                    borderLeft: dragOverItem.current === index ? '2px solid #4285F4' : undefined,
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {col.Header}
                    {col.filterable && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          const newFilter = activeFilter === col.accessor ? null : col.accessor
                          if (newFilter) setClickCoords({ x: e.clientX, y: e.clientY })
                          setActiveFilter(newFilter)
                          setFilterSearchText('')
                          if (filterSearchTimer.current) clearTimeout(filterSearchTimer.current)
                        }}
                        style={{
                          cursor: 'pointer', fontSize: 8, marginLeft: 2,
                          color: activeFilter === col.accessor ? '#4285F4' : (filterValues[col.accessor]?.length ? '#4285F4' : '#aaa'),
                          fontWeight: (activeFilter === col.accessor || filterValues[col.accessor]?.length) ? 700 : 400,
                        }}
                      >
                        ▼
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody onKeyDown={handleKeyDown}>
            {!loading && visibleRows.length === 0 && (
              <tr><td colSpan={orderedColumns.length + (onRowClick || onDelete || onDeleteRow || customActions ? 1 : 0)}><Segment><Header as="h4" textAlign="center" style={{ color: '#888' }}>{emptyMessage}</Header></Segment></td></tr>
            )}
            {visibleRows.map((row, rowIdx) => {
              const globalIdx = serverSide ? rowIdx : (currentPage - 1) * pageSize + rowIdx
              return (
                <tr key={row.id || globalIdx} style={{ backgroundColor: rowIdx % 2 === 0 ? '#F3F7FF' : '#fff', lineHeight: 1.2 }}>
                  {(onRowClick || onDelete || onDeleteRow || customActions) && (
                    <td style={{ textAlign: 'center', padding: '1px 3px', whiteSpace: 'nowrap' }} className="editable-table-actions">
                      {onDeleteRow && (
                        <Button
                          icon="trash"
                          size="mini"
                          color="red"
                          onClick={() => onDeleteRow(globalIdx)}
                          style={{ padding: 2, minWidth: 24, minHeight: 16, margin: '0 1px' }}
                        />
                      )}
                      {onRowClick && (
                        <Button
                          icon="edit"
                          size="mini"
                          color="blue"
                          onClick={(e) => { e.stopPropagation(); onRowClick(row) }}
                          style={{ padding: 2, minWidth: 24, minHeight: 16, margin: '0 1px' }}
                        />
                      )}
                      {customActions && customActions(row)}
                      {onDelete && (
                        <Button
                          icon="trash"
                          size="mini"
                          color="red"
                          onClick={(e) => { e.stopPropagation(); onDelete(row) }}
                          style={{ padding: 2, minWidth: 24, minHeight: 16, margin: '0 1px' }}
                        />
                      )}
                    </td>
                  )}
                  {orderedColumns.map(col => {
                    const isEditing = editingCell?.row === globalIdx && editingCell?.accessor === col.accessor
                    const value = getNestedValue(row, col.accessor)
                    const editable = col.editable && !col.computed

                    if (col.type === 'days') {
                      return (
                        <td key={col.accessor} style={{ padding: '2px 6px', fontSize: 11, textAlign: 'center' }}>
                          <WeekDaysToggle
                            value={value}
                            editable={editable}
                            onChange={(next) => handleCellChange(globalIdx, col.accessor, next)}
                          />
                        </td>
                      )
                    }

                    if (isEditing && editable) {
                      const cellColor = typeof col.color === 'function' ? col.color(row) : null
                      const darkBg = cellColor && isDarkColor(cellColor)
                      return (
                        <td key={col.accessor} style={{ padding: '2px 6px', fontSize: 11, ...(cellColor ? { backgroundColor: cellColor, color: darkBg ? '#fff' : undefined, fontWeight: darkBg ? 600 : undefined } : {}) }}>
                           <InlineEdit
                             value={value}
                             type={col.type || 'text'}
                             name={col.accessor}
                             options={col.options}
                             displayValue={col.labelAccessor ? getNestedValue(row, col.labelAccessor) : null}
                             onChange={(v) => handleCellChange(globalIdx, col.accessor, v)}
                             onBlur={handleCellBlur}
                             autoFocus
                           />
                        </td>
                      )
                    }

                    const cellColor = typeof col.color === 'function' ? col.color(row) : null
                    const darkBg = cellColor && isDarkColor(cellColor)
                    return (
                      <td
                        key={col.accessor}
                        onClick={() => handleCellClick(globalIdx, col)}
                        style={{
                          maxWidth: col.width, overflow: 'hidden', padding: '2px 6px',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11,
                          cursor: editable ? 'pointer' : 'default',
                          ...(cellColor ? { backgroundColor: cellColor, color: darkBg ? '#fff' : undefined, fontWeight: darkBg ? 600 : undefined } : {}),
                        }}
                      >
                        {col.type === 'boolean'
                          ? (value ? '✓' : '✗')
                          : col.type === 'password'
                            ? '••••••••'
                            : col.type === 'select'
                            ? ((col.labelAccessor && getNestedValue(row, col.labelAccessor)) || (col.options?.find(o => String(o.value) === String(value))?.text || value || ''))
                            : (isValidElement(value) ? value : (value != null ? String(value) : ''))}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </Table>
          </div>
      </div>
      {totalPages >= 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
		  <button
			className='EditableSimpleTable__pageButton'
            onClick={() => handlePageChange((displayPagination.current_page || 1) - 1)}
            disabled={(displayPagination.current_page || 1) <= 1}
            style={{
              border: '1px solid #ddd', borderRadius: 4, padding: '2px 8px', margin: '0 2px',
              cursor: (displayPagination.current_page || 1) <= 1 ? 'default' : 'pointer',
              background: '#fff', fontWeight: 400, fontSize: 11, color: '#4285F4',
              opacity: (displayPagination.current_page || 1) <= 1 ? 0.5 : 1,
            }}
          >
            ←
          </button>
          {pageNumbers.map((p, i) =>
            p === '...' ? (
              <span key={`dots-${i}`} style={{ fontSize: 11, color: '#888', padding: '2px 4px' }}>...</span>
            ) : (
			  <button
				className='EditableSimpleTable__pageButton'
                key={p}
                onClick={() => handlePageChange(p)}
                style={{
                  border: p === (displayPagination.current_page || 1) ? '2px solid #4285F4' : '1px solid #ddd',
                  borderRadius: 4, padding: '2px 8px', margin: '0 2px',
                  cursor: 'pointer', fontSize: 11,
                  background: p === (displayPagination.current_page || 1) ? '#e8f0fe' : '#fff',
                  color: p === (displayPagination.current_page || 1) ? '#000' : '#4285F4',
                  fontWeight: p === (displayPagination.current_page || 1) ? 700 : 400,
                }}
              >
                {p}
              </button>
            )
          )}
		  <button
			className='EditableSimpleTable__pageButton'
            onClick={() => handlePageChange((displayPagination.current_page || 1) + 1)}
            disabled={(displayPagination.current_page || 1) >= totalPages}
            style={{
              border: '1px solid #ddd', borderRadius: 4, padding: '2px 8px', margin: '0 2px',
              cursor: (displayPagination.current_page || 1) >= totalPages ? 'default' : 'pointer',
              background: '#fff', fontWeight: 400, fontSize: 11, color: '#4285F4',
              opacity: (displayPagination.current_page || 1) >= totalPages ? 0.5 : 1,
            }}
          >
            →
          </button>
        </div>
      )}
        </>
      )}
    </div>
  )
}

export default EditableSimpleTable
